/**
 * Google Tag Manager (GTM) Utility
 *
 * Handles GTM initialization and event tracking.
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Initialize Google Tag Manager
 * Injects the GTM script into the page if GTM_ID is configured
 */
export const initGTM = (): void => {
  const gtmId = import.meta.env.VITE_GTM_ID;

  if (!gtmId || gtmId === 'your-gtm-container-id') {
    console.warn('GTM not initialized: VITE_GTM_ID not configured');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Push GTM initialization
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Inject GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  console.log(`GTM initialized with ID: ${gtmId}`);
};

/**
 * Push a custom event to the GTM dataLayer
 *
 * @param event - Event name
 * @param data - Additional event data
 */
export const trackEvent = (event: string, data?: Record<string, any>): void => {
  if (!window.dataLayer) {
    console.warn('GTM dataLayer not available');
    return;
  }

  window.dataLayer.push({
    event,
    ...data
  });
};

/**
 * Track page view
 *
 * @param page - Page path or name
 * @param title - Page title
 */
export const trackPageView = (page: string, title?: string): void => {
  trackEvent('page_view', {
    page_path: page,
    page_title: title || document.title
  });
};

/**
 * Track user login
 *
 * @param userId - User ID
 * @param method - Login method (e.g., 'email', 'oauth')
 */
export const trackLogin = (userId: string, method: string = 'email'): void => {
  trackEvent('login', {
    user_id: userId,
    method
  });
};

/**
 * Track user registration
 *
 * @param userId - User ID
 */
export const trackSignUp = (userId: string): void => {
  trackEvent('sign_up', {
    user_id: userId
  });
};

/**
 * Track rate plan creation
 *
 * @param planId - Rate plan ID
 * @param planName - Rate plan name
 */
export const trackRatePlanCreated = (planId: number, planName: string): void => {
  trackEvent('rate_plan_created', {
    plan_id: planId,
    plan_name: planName
  });
};

/**
 * Track rate plan calculation
 *
 * @param retailPrice - Calculated retail price
 */
export const trackCalculation = (retailPrice: number): void => {
  trackEvent('price_calculated', {
    retail_price: retailPrice
  });
};